import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import pickBy from 'lodash/pickBy';

import { calculateAmericanPrice, addAmericanOdds } from '~shared/components/sport-event/utils';
import {
    WEBSOCKET_EVENT_PRICE_UPDATE,
    WEBSOCKET_EVENT_EVENTS_RESULTS,
    WEBSOCKET_EVENT_EVENT_OFF,
    WEBSOCKET_EVENT_INPLAY_UPDATE,
    WEBSOCKET_EVENT_RESCHEDULED,
    WEBSOCKET_EVENT_INPLAY_START,
} from './constants';
import { EVENT_OFF } from '~shared/components/sport-event/constants';
import { mergeMarkets, getMarketBetable } from '~shared/utils/market-utils';
import { selectionArrayToObject } from '~shared/utils/selections-utils';
import { getATOMDateTimeFormat } from '~shared/utils/date-time';

const minusOne = -1;

/**
 * Check if store or incoming events need mutating before merge of state with events.
 * Note that this function intentionally mutates the arguments.
 *
 * Preplay to Inplay transition: If a websocket message arrives for event_off and sometimes for
 * inplay_start we don't always have extra_events_info with details of the new IP event. In this case the
 * store might have a "made up" inplay event with data taken from the prePlay event. Here we trap special
 * cases where new data *must* be overwritten in the store to replace the "made up" values.
 * @param {Object} events From FE/PHP
 * @param {Object} state The current commonStore state
 * @return {Void} Void is the new void
 */
const preplayToInplayTransitionChecks = (events, state) => {
    if (Object.keys(state).length) {
        Object.keys(events).forEach((eventId) => {
            const theNewEvent = events[eventId];
            // we might not have the event in state if this is first load
            if (state[eventId] && theNewEvent.event_type === 'INPLAY') {
                /**
                 * 1. Replace competitors (instead of merge) if we have competitors in events object.
                 * (We might have "made up" competitors from the prePlay event - the names will be correct for
                 * display purposes, but will have ID's from the preplay event)
                 */
                if (Object.keys(theNewEvent.competitors)) {
                    state[eventId].competitors = {};
                }
                /**
                 * 2. Replace selections with new ones instead of merging. (Again we might have dummy ones that
                 * were created in the preplay to inplay transition
                 */
                Object.keys(theNewEvent.markets).forEach((marketId) => {
                    if (Object.keys(theNewEvent.markets[marketId].selections).length) {
                        // Possible that marketId is altID, but state has the primary default market in the store
                        const stateSelections = state[eventId]?.markets[marketId]?.selections || [];
                        // remove all dummies from corresponding state event
                        const existingSelectionIds = Object.keys(stateSelections) || [];
                        existingSelectionIds.forEach((selectionId) => {
                            if (selectionId < 0) {
                                delete state[eventId]?.markets[marketId]?.selections[selectionId];
                            }
                        });
                    }
                });
            }
        });
    }
};

/**
 * Remove old markets from an event, based on fresh data from BE.
 * This function should ONLY be called when the store is bring initially populated. If multiple components
 * are using the same store, then this will break things
 * @param {Object} events from BE
 * @param {Object} state existing state
 * @returns {Void} mutates the state
 */
const checkForDefaultMarketChange = (events, state) => {
    if (Object.keys(state).length) {
        Object.keys(events).forEach((eventId) => {
            if (state[eventId] !== undefined) {
                const eventMarketIds = Object.keys(events[eventId].markets) || [];
                const filteredMarkets = pickBy(state[eventId].markets, (_market, marketId) => (eventMarketIds.includes(marketId)));
                state[eventId].markets = filteredMarkets;
            }
        });
    }
};

/**
 * Create/update the common store structure from PHP/BE data.
 * @param {Object} events From FE/PHP
 * @param {Object} state The current commonStore state
 * @return {Object} New commonStore state
 */
export const createCommonStoreStructure = (events, state) => {
    const newState = cloneDeep(state);

    // see code comment on use of this function
    checkForDefaultMarketChange(events, newState);
    preplayToInplayTransitionChecks(events, newState);

    return merge(newState, events);
};

/**
 * Check websocket packet for events / markets / selections that need to be updated or removed
 * @param {Object} wsData From FE/PHP
 * @param {Object} state The current commonStore state (events)
 * @returns {Object} New commonStore state
 */
export const websocketUpdater = (wsData, state) => {
    let newState = {};

    /**
     * Add action handlers to this code block that return a new state
     */
    switch (wsData.action) {
        case WEBSOCKET_EVENT_RESCHEDULED: {
            newState = processRescheduledWsEvent(wsData, state);

            return newState;
        }
        case WEBSOCKET_EVENT_PRICE_UPDATE: {
            /*
            {
            "action":"price_update",
            "supplier":null,
            "events":[
                {
                    "tradable":true,
                    "suspended":null,
                    "active":true,
                    "markets":[
                        {
                        "name":"Match Result",
                        "selections":{
                            "286164":{
                                "fraction_price":"1\/5",
                                "decimal_price":1.2,
                                "tradable":true,
                                "sp_only":false,
                                "suspended":null,
                                "active":true,
                                "selection_type_id":1,
                                "outcome":null,
                                "price":1.2,
                                "extra_info":null
                            }
                        },
                        "tradable":true,
                        "restrict_cashout":false,
                        "active":true,
                        "id":8854,
                        "extra_info":null
                        }
                    ],
                    "id":2136
                }
            ]
            }
            */
            if (!isUndefined(wsData.events)) {
                newState = updateCommonStoreStructure(wsData.events, state);

                return newState;
            }

            break;
        }
        case WEBSOCKET_EVENT_INPLAY_UPDATE: {
            /*
             * will also suspend events and markets (as well as remove in case of match_status)
             */

            if (!isUndefined(wsData.events) || !isUndefined(wsData.event)) {
                let eventData = wsData.events || [wsData.event];

                newState = updateCommonStoreStructure(eventData, state);

                return newState;
            }

            break;
        }
        case WEBSOCKET_EVENT_INPLAY_START: {
            const preplayEventsToRemove = parseObjectForEventIds(wsData);

            newState = addNewInplayEvents(wsData.extra_events_info, wsData.inplay_events, state);
            newState = removeStaleEventsFromCommonStoreStructure(preplayEventsToRemove, newState);

            return newState;
        }
        case WEBSOCKET_EVENT_EVENT_OFF: {
            // suspend market...
            // we might get an inplay_start message later
            const { events: eventIds = [] } = wsData;
            if (!eventIds.length) {

                return state;
            }

            const newState = cloneDeep(state);
            eventIds.forEach((eventId) => {
                const eventMarkets = newState[eventId]?.markets;
                if (eventMarkets) {
                    Object.keys(eventMarkets).forEach((marketId) => {
                        eventMarkets[marketId].betable = false;
                    });
                }
            });

            return newState;
        }
        default: {
            // allow code to run through to next switch
        }
    }

    /**
     * Add handlers to the following switch block that DO NOT return a new state, but
     * populate the following array. They are processed at the end of the block.
     */
    let eventsToRemove = [];
    switch (wsData.action) {
        /**
         * Preplay events off become 'new' events in the BE. We remove them from the
         * Store. All following include an array of event ids in the websocket
         * update (wsData.events) that can be removed from the common store.
         */
        case WEBSOCKET_EVENT_INPLAY_START:
        case WEBSOCKET_EVENT_EVENT_OFF:
        case WEBSOCKET_EVENT_EVENTS_RESULTS: {
            eventsToRemove = parseObjectForEventIds(wsData);

            break;
        }
        default: {
            break;
        }
    }

    newState = removeStaleEventsFromCommonStoreStructure(eventsToRemove, state);

    return newState;
};

export const processRescheduledWsEvent = (wsData, commonDataEvents) => {
    /*
    {
        'action': 'rescheduled',
        'current_time': '2018-03-01 15:00:00',
        'events': [{
            'rescheduled_time': '2018-04-01 15:00:00',
            'id': 67
        }]
    }
    */
    if (!isUndefined(wsData.events)) {
        wsData.events.forEach(event => {
            if (!isUndefined(commonDataEvents[event.id])) {
                commonDataEvents[event.id].scheduled_start = event.rescheduled_time;
            }
        });
    }

    return { ...commonDataEvents };

};

/**
 * Parse websocket payload for an 'events' property and construct an array
 * of event ids
 * @param {Object} wsData the websocket payload
 * @returns {Array} id's of events
 */
export const parseObjectForEventIds = wsData => {
    /*
    {
        "action": "events_results",
        "events": [604630]
    }
    */
    /*
    {
        "action": "event_off",
        "inplay_events": {},
        "events": [604424],
        "inplay_live": {}
    }
    */
    /*
    {
        'action':'inplay_start',
        'events':[
            1,
            2083,
            2108,
            99999999999
        ],
        'inplay_events':{
            1:5,
            2:6
        },
        'inplay_live':{
        },
        'extra_events_info':[
        ]
    }
    */
    const eventIds = [];
    if (!isUndefined(wsData.events)) {
        wsData.events.forEach(eventId => {
            eventIds.push(eventId);
        });
    }

    return eventIds;
};

/**
 * Remove stale events / markets / selections from the common store
 * @param {Object} eventsToRemove array of event Id's
 * @param {Object} state existing state
 * @returns {Object} new state
 */
export const removeStaleEventsFromCommonStoreStructure = (eventsToRemove, state) => {
    const updatedState = { ...state };

    if (eventsToRemove.length) {
        eventsToRemove.forEach(eventId => {
            if (!isUndefined(updatedState[eventId])) {
                delete updatedState[eventId];
            }
        });
    }

    return updatedState;
};

const completeStatus = [
    'canceled', 'abandoned', 'walkover', 'retired', 'ended', 'manually_deactivated',
];

export const eventStatusIsComplete = event => {
    if (isUndefined(event.match_status)) {
        return false;
    }

    return completeStatus.indexOf(event.match_status.toLowerCase()) !== minusOne;
};

/**
 * Truncate a time string from mm:ss to mm:00 to minimise changes in the state
 * @param {String} eventExtraInfoTime the time (from inplay_update websocket message)
 * @returns {String} truncated time
 */
 const truncateTimeString = (eventExtraInfoTime = '00:00') => {
    const [minutes] = eventExtraInfoTime.split(':');

    return `${minutes}:00`;
};

/**
 * Update the common store structure.
 *
 * @param {Object} events Events data from web socket updates
 * @param {Object} state The current state (events)
 * @return {Object} New state
 */
export const updateCommonStoreStructure = (events = [], state) => {
    const updatedState = { ...state };

    events.forEach(event => {
        const { id: eventId, is_inplay: isInplay = false} = event;
        if (eventStatusIsComplete(event)) {
            delete updatedState[eventId];
        } else if (!isUndefined(state[eventId])) {
            let eventBetable = state[eventId].betable;
            if (!isUndefined(event.betable)) {
                eventBetable = event.betable;
            } else if(!isUndefined(event.suspended)) {
                eventBetable = !event.suspended;
            } else if (!isUndefined(event.tradable)) {
                eventBetable = event.tradable;
            }

            updatedState[eventId] = {
                ...state[eventId],
                betable: eventBetable,
            };

            if (event.extra_info) {
                updatedState[eventId].extra_info = {
                    ...updatedState[eventId].extra_info,
                    ...event.extra_info,
                    time: truncateTimeString(event.extra_info.time),
                };
            }

            if (event.match_status) {
                updatedState[eventId].match_status = event.match_status;
            }

            if (!isUndefined(event.markets)) {
                if (!isUndefined(updatedState[eventId].markets)) {
                    updatedState[eventId].markets = {
                        ...updatedState[eventId].markets,
                    };
                }

                // we can get 2 markets in one update, we need to merge them here.
                mergeMarkets(event.markets).forEach(market => {
                    const marketId = market.id;
                    if (isUndefined(state[eventId].markets[marketId])) {
                        // we should not be updating any markets
                        // that do not already exist in the store
                        return;
                    }
                    const marketBetable = getMarketBetable(market);

                    updatedState[eventId].markets[marketId] = {
                        ...state[eventId].markets[marketId],
                        ...updatedState[eventId].markets[marketId],
                        betable: marketBetable,
                    };

                    const { default_line_selections: defaultLineChange = [], selections: marketSelections = {} } = market;
                    const defaultLineHasChanged = Boolean(defaultLineChange.length);
                    if (defaultLineHasChanged) {
                        // default_line_selections will be empty except when the default line changes.
                        // if there is selecion id's in 'default_line_selections' then those selections
                        // will always be included in the same packet.
                        updatedState[eventId].markets[marketId].default_line_selections = defaultLineChange;
                        defaultLineChange.forEach(selectionId => {
                            if (isUndefined(updatedState[eventId].markets[marketId].selections)) {
                                updatedState[eventId].markets[marketId].selections = {};
                            }
                            if (isUndefined(state[eventId].markets[marketId].selections)) {
                                state[eventId].markets[marketId].selections = {};
                            }
                            if (isUndefined(state[eventId].markets[marketId].selections[selectionId])) {
                                const selection = marketSelections[selectionId];
                                if(selection) {
                                    // if the selection does not already exist in the store add it.
                                    // 'id' and 'special_odds_value' are attributes of selections when
                                    // they come from the normal endpoint but not in selections that come from WS
                                    // so this adds them.
                                    state[eventId].markets[marketId].selections[selectionId] = {
                                        id: selectionId,
                                        special_odds_value: selection.specialoddsvalue,
                                        ...selection,
                                    };
                                }
                            }
                        });
                    }

                    if (!isUndefined(market.selections)) {
                        if (!isUndefined(updatedState[eventId].markets[marketId].selections)) {
                            updatedState[eventId].markets[marketId].selections = {
                                ...updatedState[eventId].markets[marketId].selections,
                            };
                        }

                        if(isInplay && market.selections.length) {
                            // market.selections inplay updates is formatted as an array.
                            // this changes it to match the preplay payload.
                            market.selections = selectionArrayToObject(market.selections);
                        }

                        // handle updates to an event with "dummy" selections
                        // if we had a PP -> IP transition (inplay_start) with no active markets at that time, we
                        // constructed "dummy" selections, with id's < 0; Now we replace the dummys with the "real"
                        // ones we got from the websocket message
                        // note that we do not account for a single dummy selection - one dummy assumes all are
                        const existingMarketSelections = Object.keys(updatedState[eventId].markets[marketId].selections) || [];
                        const hasDummySelections = existingMarketSelections.some((selectionId) => (selectionId < 0));
                        if (hasDummySelections) {
                            // replace them
                            // note that this applies to selections in a price_update. While we could call
                            // the selection ordering endpoint, we have the selection_type_id in these
                            // messages and can use them to order selections. (Mojo has the selection_type_id
                            // values hardcoded apparently so this is not pretty but safe to do.)
                            const newSelections = {};
                            Object.keys(market.selections).forEach((selectionId) => {
                                const selection = market.selections[selectionId];
                                const betable = (typeof selection.betable !== 'undefined' && selection.betable)
                                    || !selection.suspended;
                                newSelections[selectionId] = {
                                    ...selection,
                                    id: parseInt(selectionId, 10),
                                    betable,
                                    suspended: !betable,
                                    order: selection.selection_type_id,
                                    american_price: calculateAmericanPrice(selection.decimal_price),
                                };
                            });
                            updatedState[eventId].markets[marketId].selections = newSelections;

                            // early return - no need to loop through selections below
                            return;
                        }

                        // this handles updates to market selections we already have
                        Object.keys(market.selections).forEach(selectionId => {
                            const selection = market.selections[selectionId];
                            let selectionBetable;
                            if (!isUndefined(selection.betable)) {
                                selectionBetable = selection.betable;
                            } else {
                                selectionBetable = !selection.suspended;
                            }

                            if (!isUndefined(updatedState[eventId].markets[marketId].selections)
                                && !isUndefined(updatedState[eventId].markets[marketId].selections[selectionId])
                            ) {
                                updatedState[eventId].markets[marketId].selections[selectionId] = {
                                    ...state[eventId].markets[marketId].selections[selectionId],
                                    decimal_price: selection.decimal_price,
                                    fraction_price: selection.fraction_price,
                                    american_price: calculateAmericanPrice(selection.decimal_price),
                                    betable: selectionBetable,
                                    suspended: !selectionBetable, // Retrocompatibility
                                };
                            }
                        });
                    }
                });
            }
        }
    });

    return updatedState;
};

/**
 * Generate an inplay event based on a preplay event.
 * This allows us to cater for a websocket inplay_start message which has no extra_event_info (which would
 * carry the inplay event info)
 * @todo https://dedsert.atlassian.net/browse/BT-10062
 * @param {Number} eventId the event id
 * @param {Object} preplayEvent the associated preplay event
 * @returns {Object} an inplat event
 */
const generateInplayEvent = (eventId, preplayEvent) => {
    // we assume that the default market id is the only/first entry in the markets object
    const [defaultMarketId = null] = Object.keys(preplayEvent.markets);
    if (defaultMarketId === null) {

        // nothing to do
        return false;
    }
    const preplaySelections = Object.values(preplayEvent.markets[defaultMarketId].selections);
    if (!preplaySelections.length) {

        // nothing to do
        return false;
    }

    const event = {
        name: preplayEvent.name,
        market_suspended: false,
        selection_schema: preplayEvent.markets[defaultMarketId].selection_schema,
        selections: [],
        market_id: parseInt(defaultMarketId, 10),
        suspended: false,
        grouped_market_ids: [],
        id: eventId,
    };

    preplaySelections.forEach((preplaySelection) => {
        event.selections.push({
            ...preplaySelection,
            id: -1 * preplaySelection.id,
            selection_type: preplaySelection.type, // used in IP
            active: false,
            tradable: false,
            suspended: true,
            betable: false, // used in IP
        });
    });

    return event;
};

export const addNewInplayEvents = (events, preplayMapping, state) => {
    const mappedEvents = Object.keys(preplayMapping).reduce((mappedEvents, preplayEventId) => {
        const preplayEvent = state[preplayEventId];

        if (isUndefined(preplayEvent)) {
            return mappedEvents;
        }

        const inplayEventId = preplayMapping[preplayEventId];
        let inplayEvent = events[inplayEventId];

        if (isUndefined(inplayEvent)) {
            // if there are no active selections @ inplay_start the websocket message gives us no
            // event_extra_info - we need to make up an event
            inplayEvent = generateInplayEvent(inplayEventId, preplayEvent);
            if (inplayEvent === false) {

                // can't proceed
                return mappedEvents;
            }
        }

        const selections = inplayEvent.selections.reduce((selections, selection) => {
            const betable = !isUndefined(selection.betable)
                ? selection.betable
                : !selection.suspended;

            selections[selection.id] = {
                ...selection,
                extra_info: {
                    ...selection.extra_info,
                    participant_id: selection.id,
                },
                betable,
                type: selection.selection_type, // field doesn't exist in WS message
            };

            return selections;
        }, {});

        // We do not get competitor 'order' in our inplay_start message - required fror sorting, so we must
        // use the preplay event ones. Note that this is only or transitioning event from PP -> IP, not on
        // page load
        const inplayCompetitors = { ...preplayEvent.competitors };

        // no selection_schema supplied for the inplay_start message - we'll use the preplay one and assume
        // it is the same
        // @todo AM-4687 we will need to see if this assumption is wrong for alt default markets (darts?)
        const preplaySelectionSchema = preplayEvent.markets[inplayEvent.market_id].selection_schema;

        mappedEvents[inplayEventId] = {
            id: inplayEventId,
            name: inplayEvent.name,
            event_slug_i18n: preplayEvent.event_slug_i18n,
            event_type: 'INPLAY',
            betable: !inplayEvent.suspended,
            is_inplay: true,
            inplay: true,
            is_outright: preplayEvent.is_outright || false,
            market_order: preplayEvent.market_order,
            // @TODO use scheduled_start
            scheduled_date: preplayEvent.scheduled_date,
            scheduled_start: preplayEvent.scheduled_start,
            start_time: getATOMDateTimeFormat(),
            sport_name: preplayEvent.sport_name,
            sport_slug: preplayEvent.sport_slug,
            sport_slug_i18n: preplayEvent.sport_slug_i18n || '',
            tournament_slug_i18n: preplayEvent.tournament_slug_i18n || '',
            category_id: preplayEvent.category_id || 0,
            category_slug: preplayEvent.category_slug || '',
            category_slug_i18n: preplayEvent.category_slug_i18n || '',
            category_name: preplayEvent.category_name || '',
            tournament_id: preplayEvent.tournament_id || '',
            tournament_display_name: preplayEvent.tournament_display_name || '',
            tournament_slug: preplayEvent.tournament_slug || '',
            tournament_logos: preplayEvent.tournament_logos,
            is_streaming_available: preplayEvent.is_streaming_available,
            market_id: inplayEvent.market_id,
            markets: {
                [inplayEvent.market_id]: {
                    id: inplayEvent.market_id,
                    betable: !inplayEvent.market_suspended,
                    ew_terms: preplayEvent.ew_terms,
                    selection_schema: preplaySelectionSchema,
                    selections,
                },
            },
            competitors: inplayCompetitors,
            match_status: EVENT_OFF,
            extra_info: {
                event_id: inplayEventId,
                score: '0:0',
                time: '00:00',
            },
            usesThreeMarketView: preplayEvent.usesThreeMarketView,
            isUsFormat: preplayEvent.isUsFormat,
            addedFromWs: true,
            event_url: `/inplay/match_markets/${inplayEventId}`,
        };

        return mappedEvents;
    }, {});

    return {...state, ...mappedEvents};
};


export const addPriceToEventsSelections = (events = {}) => {
    if (events) {
        Object.keys(events).forEach((eventId) => {
            if (events[eventId] && events[eventId].markets) {
                Object.keys(events[eventId].markets).forEach((marketId) => {
                    if (events[eventId].markets[marketId] && events[eventId].markets[marketId].selections) {
                        Object.keys(events[eventId].markets[marketId].selections).forEach((selectionId) => {
                            addAmericanOdds(events[eventId].markets[marketId].selections[selectionId]);
                        });
                    }
                });
            }
        });
    }
};

export const updateEvents = (events, state) => {
    const updatedState = cloneDeep(state);
    Object.keys(events).forEach(eventId => {
        updatedState[eventId].markets = events[eventId].markets;
        updatedState[eventId].market_id = events[eventId].market_id;
        updatedState[eventId].marketHeader = events[eventId].marketHeader;
    });

    return updatedState;
};
